
import { BenchmarkFilters, BenchmarkData, LocationGroupData, PriceData, MultiProductBenchmarkFilters, MultiProductBenchmarkData, ProductComparisonData } from './types';
import { LOCATION_GROUP_NAMES, LOCATION_GROUP_ORDER } from './constants';
import { fetchDataForLocationGroup } from './dataFetcher';
import { calculateMinPrice, calculateWeightedAverage } from './priceCalculator';
import { fetchChipChipMultiProductData, ChipChipPriceData } from '../chipchip/chipchipService';
import { aggregateDataByFrequency, aggregateDates } from './dataAggregator';

export async function getBenchmarkData(filters: BenchmarkFilters): Promise<BenchmarkData> {
  let allData: PriceData[] = [];
  
  // Fetch data from all selected location groups
  for (const locationGroup of filters.locationGroups) {
    const locationData = await fetchDataForLocationGroup(locationGroup, filters);
    allData.push(...locationData);
  }

  // Aggregate data based on frequency
  allData = aggregateDataByFrequency(allData, filters.frequency);

  // Get all unique dates and sort them
  let allDates = [...new Set(allData.map(item => item.date))].sort();
  
  // Aggregate dates based on frequency
  allDates = aggregateDates(allDates, filters.frequency);

  // Group data by location groups and dates in the specified order
  const locationGroups: LocationGroupData[] = [];

  // Process location groups in the specified order
  const orderedLocationGroups = LOCATION_GROUP_ORDER.filter(group => 
    filters.locationGroups.includes(group)
  );

  for (const locationGroup of orderedLocationGroups) {
    const groupData = allData.filter(item => item.source === locationGroup);
    const groupName = LOCATION_GROUP_NAMES[locationGroup as keyof typeof LOCATION_GROUP_NAMES];

    const dates: string[] = [];
    const minPrices: number[] = [];
    const avgPrices: number[] = [];
    const detailedData: PriceData[][] = [];

    for (const date of allDates) {
      const dateData = groupData.filter(item => item.date === date);
      
      // Always include the date, even if no data exists for this location group
      dates.push(date);
      
      if (dateData.length > 0) {
        const minPrice = calculateMinPrice(dateData);
        const weightedAvgPrice = calculateWeightedAverage(dateData);
        
        minPrices.push(minPrice);
        avgPrices.push(weightedAvgPrice);
        detailedData.push(dateData);
      } else {
        // No data for this date, push null/empty values
        minPrices.push(0);
        avgPrices.push(0);
        detailedData.push([]);
      }
    }

    // Always include location groups, even if they have no data for some dates
    locationGroups.push({
      groupName,
      dates,
      minPrices,
      avgPrices,
      detailedData,
    });
  }

  return {
    locationGroups,
    allDates,
  };
}

export async function getMultiProductBenchmarkData(filters: MultiProductBenchmarkFilters): Promise<MultiProductBenchmarkData> {
  const products: ProductComparisonData[] = [];
  
  // Check if ChipChip data is requested
  const includeChipChip = filters.locationGroups.includes('chipchip_sales');
  let chipchipData: ChipChipPriceData[] = [];
  
  // Fetch ChipChip data for all products if requested
  if (includeChipChip) {
    console.log('Fetching ChipChip data for products:', filters.products);
    chipchipData = await fetchChipChipMultiProductData(
      filters.products,
      filters.dateFrom,
      filters.dateTo
    );
  }
  
  // Process each product individually
  for (const product of filters.products) {
    const productFilters: BenchmarkFilters = {
      ...filters,
      product,
      // Remove chipchip_sales from locationGroups for regular benchmark data
      locationGroups: filters.locationGroups.filter(lg => lg !== 'chipchip_sales')
    };
    
    console.log(`Processing product: ${product}, Regular location groups:`, productFilters.locationGroups);
    const productData = await getBenchmarkData(productFilters);
    console.log(`Regular market data found for ${product}:`, productData.locationGroups.map(g => g.groupName));
    
    // Add ChipChip data if available for this product
    if (includeChipChip) {
      const productChipChipData = chipchipData.filter(item => 
        item.product_name.toLowerCase().includes(product.toLowerCase())
      );
      
      if (productChipChipData.length > 0) {
        // Create ChipChip location group data
        const chipchipLocationGroup: LocationGroupData = {
          groupName: 'ChipChip Sales',
          dates: [],
          minPrices: [],
          avgPrices: [],
          detailedData: []
        };
        
        // Group ChipChip data by date
        const dateMap = new Map<string, ChipChipPriceData[]>();
        productChipChipData.forEach(item => {
          if (!dateMap.has(item.date)) {
            dateMap.set(item.date, []);
          }
          dateMap.get(item.date)!.push(item);
        });
        
        // Process each date
        Array.from(dateMap.entries())
          .sort(([a], [b]) => a.localeCompare(b))
          .forEach(([date, items]) => {
            chipchipLocationGroup.dates.push(date);
            
            const prices = items.map(item => item.price);
            const minPrice = Math.min(...prices);
            const avgPrice = prices.reduce((a, b) => a + b, 0) / prices.length;
            
            chipchipLocationGroup.minPrices.push(minPrice);
            chipchipLocationGroup.avgPrices.push(avgPrice);
            chipchipLocationGroup.detailedData.push(items as PriceData[]);
          });
        
        // Add ChipChip data to product location groups
        productData.locationGroups.push(chipchipLocationGroup);
        
        // Update all dates to include ChipChip dates
        const allDates = [...new Set([...productData.allDates, ...chipchipLocationGroup.dates])].sort();
        productData.allDates = allDates;
      }
    }
    
    // Calculate additional metrics for this product (including ChipChip data)
    const allPrices: number[] = [];
    productData.locationGroups.forEach(group => {
      allPrices.push(...group.minPrices, ...group.avgPrices);
    });
    
    const averagePrice = allPrices.length > 0 ? allPrices.reduce((a, b) => a + b, 0) / allPrices.length : 0;
    const minPrice = allPrices.length > 0 ? Math.min(...allPrices) : 0;
    const maxPrice = allPrices.length > 0 ? Math.max(...allPrices) : 0;
    
    // Calculate volatility score (coefficient of variation)
    const mean = averagePrice;
    const variance = allPrices.length > 0 ? 
      allPrices.reduce((acc, price) => acc + Math.pow(price - mean, 2), 0) / allPrices.length : 0;
    const standardDeviation = Math.sqrt(variance);
    const volatilityScore = mean > 0 ? (standardDeviation / mean) * 100 : 0;
    
    products.push({
      productName: product,
      locationGroups: productData.locationGroups,
      allDates: productData.allDates,
      averagePrice,
      priceRange: { min: minPrice, max: maxPrice },
      volatilityScore,
    });
  }
  
  // Calculate cross-product analysis
  const crossProductAnalysis = calculateCrossProductAnalysis(products);
  
  return {
    products,
    crossProductAnalysis,
  };
}

function calculateCrossProductAnalysis(products: ProductComparisonData[]) {
  const n = products.length;
  const correlationMatrix: number[][] = Array(n).fill(null).map(() => Array(n).fill(0));
  
  // Calculate correlation matrix (simplified version)
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      if (i === j) {
        correlationMatrix[i][j] = 1;
      } else {
        // Simple correlation based on average prices (can be enhanced)
        const priceA = products[i].averagePrice;
        const priceB = products[j].averagePrice;
        const correlation = Math.abs(priceA - priceB) / Math.max(priceA, priceB);
        correlationMatrix[i][j] = Math.max(0, 1 - correlation);
      }
    }
  }
  
  // Market positioning
  const marketPositioning: Record<string, 'low' | 'medium' | 'high'> = {};
  const avgPrices = products.map(p => p.averagePrice);
  const sortedPrices = [...avgPrices].sort((a, b) => a - b);
  const lowThreshold = sortedPrices[Math.floor(sortedPrices.length * 0.33)];
  const highThreshold = sortedPrices[Math.floor(sortedPrices.length * 0.67)];
  
  products.forEach(product => {
    if (product.averagePrice <= lowThreshold) {
      marketPositioning[product.productName] = 'low';
    } else if (product.averagePrice >= highThreshold) {
      marketPositioning[product.productName] = 'high';
    } else {
      marketPositioning[product.productName] = 'medium';
    }
  });
  
  // Arbitrage opportunities
  const arbitrageOpportunities = [];
  for (let i = 0; i < products.length; i++) {
    for (let j = i + 1; j < products.length; j++) {
      const productA = products[i];
      const productB = products[j];
      const priceDifference = Math.abs(productA.averagePrice - productB.averagePrice);
      
      if (priceDifference > Math.max(productA.averagePrice, productB.averagePrice) * 0.2) {
        const cheaper = productA.averagePrice < productB.averagePrice ? productA : productB;
        const expensive = productA.averagePrice > productB.averagePrice ? productA : productB;
        
        arbitrageOpportunities.push({
          productA: cheaper.productName,
          productB: expensive.productName,
          priceDifference,
          opportunity: `${cheaper.productName} is ${((priceDifference / expensive.averagePrice) * 100).toFixed(1)}% cheaper than ${expensive.productName}`,
        });
      }
    }
  }
  
  return {
    correlationMatrix,
    marketPositioning,
    arbitrageOpportunities,
  };
}
